import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormInput, FormSelect } from '../components/FormControls.jsx';
import { ViewAsUserButton } from '../components/ViewAsUserButton.jsx';
import { ROLE_LABELS, ROLES } from '../utils/roles.js';
import { clientLabel } from '../utils/clientLabel.js';

export function SuperAdminUsersPage() {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, current_page: 1, last_page: 1 });
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [roleFilter, setRoleFilter] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async (page = 1, term = search) => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ page: String(page), per_page: '100' });
    if (term) {
      params.set('search', term);
    }
    if (roleFilter) {
      params.set('role', roleFilter);
    }
    try {
      const data = await apiJson(`/api/super-admin/users?${params}`);
      setUsers(data.data ?? []);
      setMeta({
        total: data.total ?? 0,
        current_page: data.current_page ?? 1,
        last_page: data.last_page ?? 1,
      });
    } catch (err) {
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fromUrl = searchParams.get('search');
    if (fromUrl != null) {
      setSearch(fromUrl);
    }
    void load(1, fromUrl ?? search);
  }, [searchParams]);

  const updateRole = async (userId, role) => {
    try {
      await apiJson(`/api/super-admin/users/${userId}`, {
        method: 'PUT',
        body: { role },
      });
      await load(meta.current_page);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="stack">
      <h2 data-tour="page-title">Login accounts</h2>
      <p className="muted small">
        Everyone who can sign in: agency staff, portal clients, super admin. A new row appears here only after you
        create a <strong>portal login</strong> on the client page — not when you only add a client record under{' '}
        <strong>Clients</strong>.
      </p>
      {error ? <p className="err">{error}</p> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      <form
        className="row g-2 align-items-end mb-3"
        data-tour="users-filters"
        onSubmit={(e) => {
          e.preventDefault();
          void load(1);
        }}
      >
        <div className="col-md-5">
          <label htmlFor="user-search" className="form-label">
            Search
          </label>
          <FormInput
            id="user-search"
            placeholder="Name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="user-role-filter" className="form-label">
            Role
          </label>
          <FormSelect
            id="user-role-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">All roles</option>
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </FormSelect>
        </div>
        <div className="col-md-3">
          <button type="submit" className="btn btn-primary w-100">
            Search
          </button>
        </div>
      </form>

      <div className="table-wrap" data-tour="users-table">
        <table className="table table-striped table-hover data-table mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Linked client</th>
              <th style={{ minWidth: '16rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{ROLE_LABELS[u.role] ?? u.role}</td>
                <td>{u.organization?.name ?? '—'}</td>
                <td>{clientLabel(u.client) || '—'}</td>
                <td>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {u.can_impersonate ? <ViewAsUserButton userId={u.id} /> : null}
                    <FormSelect
                      value={u.role}
                      onChange={(e) => updateRole(u.id, e.target.value)}
                      disabled={u.role === ROLES.SUPER_ADMIN}
                      aria-label={`Role for ${u.email}`}
                      style={{ maxWidth: '11rem' }}
                    >
                      {Object.entries(ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </FormSelect>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {meta.last_page > 1 ? (
        <div className="row-actions">
          <button
            type="button"
            className="btn-secondary"
            disabled={meta.current_page <= 1}
            onClick={() => void load(meta.current_page - 1)}
          >
            Previous
          </button>
          <span className="muted small">
            Page {meta.current_page} of {meta.last_page} ({meta.total} accounts)
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={meta.current_page >= meta.last_page}
            onClick={() => void load(meta.current_page + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
